/**
 * LoggerService - Centralized logging with data sanitization
 *
 * SECURITY: This service sanitizes sensitive data before logging
 * and controls log verbosity based on environment.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogConfig {
  level: LogLevel;
  enableInProduction: boolean;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

// Sensitive field patterns to redact
const SENSITIVE_PATTERNS = /password|token|secret|key|auth|credential|api_key|bearer|ssn|credit_card|cvv|pin|authorization/i;

// Fields to completely redact (show [REDACTED])
const REDACT_FIELDS = [
  'password',
  'passwordConfirmation',
  'accessToken',
  'refreshToken',
  'access_token',
  'refresh_token',
  'apiKey',
  'api_key',
  'secret',
  'authorization',
  'bearer',
  'token',
];

class LoggerServiceClass {
  private config: LogConfig;

  constructor() {
    const isProduction = import.meta.env.PROD || import.meta.env.MODE === 'production';

    this.config = {
      level: isProduction ? 'error' : 'debug',
      enableInProduction: false,
    };
  }

  /**
   * Sanitize data by redacting sensitive fields
   */
  private sanitize(data: unknown): unknown {
    if (data === null || data === undefined) {
      return data;
    }

    if (typeof data === 'string') {
      // Check if string looks like a token (long base64-ish string)
      if (data.length > 20 && /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/.test(data)) {
        return '[TOKEN_REDACTED]';
      }
      // Check for email-like patterns and partially mask
      if (/@/.test(data) && /\.(com|org|net|io|dev)$/i.test(data)) {
        const [local, domain] = data.split('@');
        return `${local.substring(0, 2)}***@${domain}`;
      }
      return data;
    }

    if (Array.isArray(data)) {
      return data.map(item => this.sanitize(item));
    }

    if (typeof data === 'object') {
      const sanitized: Record<string, unknown> = {};

      for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
        // Check if key matches sensitive pattern
        if (SENSITIVE_PATTERNS.test(key) || REDACT_FIELDS.includes(key)) {
          sanitized[key] = '[REDACTED]';
        } else if (typeof value === 'string' && value.length > 0) {
          sanitized[key] = this.sanitize(value);
        } else if (typeof value === 'object' && value !== null) {
          sanitized[key] = this.sanitize(value);
        } else {
          sanitized[key] = value;
        }
      }

      return sanitized;
    }

    return data;
  }

  /**
   * Check if logging is allowed for the given level
   */
  private shouldLog(level: LogLevel): boolean {
    const isProduction = import.meta.env.PROD || import.meta.env.MODE === 'production';

    // In production, only allow error level unless explicitly enabled
    if (isProduction && !this.config.enableInProduction && level !== 'error') {
      return false;
    }

    return LOG_LEVELS[level] >= LOG_LEVELS[this.config.level];
  }

  /**
   * Format log message with timestamp
   */
  private formatMessage(level: LogLevel, message: string): string {
    const timestamp = new Date().toISOString();
    return `[${timestamp}] [${level.toUpperCase()}] ${message}`;
  }

  /**
   * Debug level logging - development only
   */
  debug(message: string, ...args: unknown[]): void {
    if (!this.shouldLog('debug')) return;

    const sanitizedArgs = args.map(arg => this.sanitize(arg));
    console.log(this.formatMessage('debug', message), ...sanitizedArgs);
  }

  /**
   * Info level logging
   */
  info(message: string, ...args: unknown[]): void {
    if (!this.shouldLog('info')) return;

    const sanitizedArgs = args.map(arg => this.sanitize(arg));
    console.info(this.formatMessage('info', message), ...sanitizedArgs);
  }

  /**
   * Warning level logging
   */
  warn(message: string, ...args: unknown[]): void {
    if (!this.shouldLog('warn')) return;

    const sanitizedArgs = args.map(arg => this.sanitize(arg));
    console.warn(this.formatMessage('warn', message), ...sanitizedArgs);
  }

  /**
   * Error level logging - always allowed
   */
  error(message: string, ...args: unknown[]): void {
    if (!this.shouldLog('error')) return;

    const sanitizedArgs = args.map(arg => this.sanitize(arg));
    console.error(this.formatMessage('error', message), ...sanitizedArgs);
  }

  /**
   * Group related logs together
   */
  group(label: string): void {
    if (!this.shouldLog('debug')) return;
    console.group(label);
  }

  /**
   * End a log group
   */
  groupEnd(): void {
    if (!this.shouldLog('debug')) return;
    console.groupEnd();
  }

  /**
   * Set log level dynamically
   */
  setLevel(level: LogLevel): void {
    this.config.level = level;
  }

  /**
   * Enable logging in production (use with caution)
   */
  enableProductionLogging(enable: boolean): void {
    this.config.enableInProduction = enable;
  }
}

// Export singleton instance
export const LoggerService = new LoggerServiceClass();

// Export for direct use
export default LoggerService;
